import { useMyAccountQuery } from '@kijiji/generated/graphql-types'
import Script, { type ScriptProps } from 'next/script'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { type FC, type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import {
  BannerSellCarBodyText,
  CarsBannerBgImage,
  CarsBannerBodyText,
  CarsBannerContainer,
  CarsBannerContentWrapper,
  CarsBannerHeadlineSubText,
  CarsBannerHeadlineText,
  CarsBannerICOCTA,
  CarsBannerPostAdCTA,
  CarsBannerTabsOuterWrapper,
  CarsBannerTextWrapper,
  LargeImageWrapper,
  SmallImageWrapper,
} from '@/components/brp/top-cars-banner/styled'
import { TopCarsBannerTabs } from '@/components/brp/top-cars-banner/tabs/TopCarsBannerTabs'
import { ImageCustom } from '@/components/shared/image'
import { AC_URL, AUTOCAN_CTA_IDS } from '@/constants/ico'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { ROUTES } from '@/constants/routes'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { AUTO_BRP_ICO_SELL_MY_CAR_UTM_TRACKING } from '@/features/autocanada/constants/ico'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'
import { getIcoScriptURL, openIcoModal } from '@/utils/autoCanadaHelper'
import { sendToLogger } from '@/utils/sendToLogger'
import cashOfferImageLarge from '~/public/next-assets/images/brp-landing/cash-offer-large-image.png'
import cashOfferImageSmall from '~/public/next-assets/images/brp-landing/cash-offer-small-imge.png'
import listCarImageLarge from '~/public/next-assets/images/brp-landing/list-car-large-image.png'
import listCarImageSmall from '~/public/next-assets/images/brp-landing/list-car-small-image.png'

type TopCarsBannerProps = {
  children: ReactNode
}

export const TopCarsBanner: FC<TopCarsBannerProps> = ({ children }) => {
  const { t } = useTranslation(TRANSLATION_KEYS.SRP)
  const { colors, spacing } = useTheme()
  const { routeLocale } = useLocale()
  const { status } = useSession()
  const { data } = useMyAccountQuery({ skip: !isUserAuthenticated(status) })
  const profileData = data?.myAccount || undefined
  const TAB1_ID = 'tab1',
    TAB2_ID = 'tab2'
  const [tab, setTab] = useState(TAB1_ID)

  const handleIcoScriptError: ScriptProps['onError'] = (err) => {
    sendToLogger(err, { fingerprint: ['TopCarsBanner'] })
  }

  const handleIcoCtaClick = useCallback(() => {
    trackEvent({
      action: GA_EVENT.GetOfferBegin,
      label: 'page=brp_landing_page;section=sell_your_car',
    })

    openIcoModal({
      routeLocale,
      icoDataProps: { ctaId: AUTOCAN_CTA_IDS.ICO_LANDING_PAGE_SMC },
      profileData,
      utmParams: AUTO_BRP_ICO_SELL_MY_CAR_UTM_TRACKING,
    })
  }, [routeLocale, profileData])
  const ICO_SCRIPT_URL = useMemo(() => getIcoScriptURL(), [])

  const handleClickTab = (e: MouseEvent, tabId: string) => {
    e.preventDefault()
    setTab(tabId)
  }

  const sellYourCar = () => {
    const sections = [
      {
        smallImage: listCarImageSmall,
        largeImage: listCarImageLarge,
        headlineTranslationKey: 'top_cars_trucks.sell_car.list_car.headline',
        bodyTranslationKey: 'top_cars_trucks.sell_car.list_car.body',
        button: (
          <CarsBannerPostAdCTA
            namespace={TRANSLATION_KEYS.SRP}
            postAdGAEventLabel="page=brp_landing_page;section=sell_your_car"
            testId="sell-car-link-post-ad"
            translationContentKey="top_cars_trucks.sell_car.list_car.button"
            callbackUrl="/p-post-ad.html?categoryId=174"
            route={ROUTES.POST_AD_AUTOS}
          />
        ),
      },
      {
        smallImage: cashOfferImageSmall,
        largeImage: cashOfferImageLarge,
        headlineTranslationKey: 'top_cars_trucks.sell_car.ico.headline',
        bodyTranslationKey: 'top_cars_trucks.sell_car.ico.body',
        button: (
          <CarsBannerICOCTA variant="secondary" onClick={handleIcoCtaClick}>
            {t('top_cars_trucks.sell_car.ico.button')}
          </CarsBannerICOCTA>
        ),
      },
    ]

    return (
      <Spacing
        mTop={spacing.default}
        mBottom={spacing.default}
        medium={{ mTop: '0', mBottom: '0' }}
      >
        <Flex
          flexDirection="column"
          rowGap={spacing.husky}
          media={{
            medium: { flexDirection: 'row', columnGap: spacing.large },
            large: { columnGap: spacing.giant },
          }}
        >
          {sections.map((section, i) => (
            <Spacing key={i}>
              <Flex>
                <Flex media={{ medium: { alignItems: 'center' } }}>
                  <SmallImageWrapper>
                    <ImageCustom
                      alt={t(section.headlineTranslationKey)}
                      srcImage={section.smallImage}
                      width={100}
                      height={100}
                      skipNextImage
                    />
                  </SmallImageWrapper>
                  <LargeImageWrapper>
                    <ImageCustom
                      alt={t(section.headlineTranslationKey)}
                      srcImage={section.largeImage}
                      width={180}
                      height={180}
                      skipNextImage
                    />
                  </LargeImageWrapper>
                </Flex>
                <Flex media={{ medium: { alignItems: 'center' } }}>
                  <Spacing mLeft={spacing.default} large={{ mLeft: spacing.large }}>
                    <HeadlineText as="h5" weight="semiBold" size={{ medium: 'large' }}>
                      {t(section.headlineTranslationKey)}
                    </HeadlineText>
                    <Spacing
                      mTop={spacing.defaultSmall}
                      mBottom={spacing.defaultSmall}
                      pBottom={spacing.mini}
                    >
                      <BannerSellCarBodyText size="medium">
                        {t(section.bodyTranslationKey)}
                      </BannerSellCarBodyText>
                    </Spacing>
                    {section.button}
                  </Spacing>
                </Flex>
              </Flex>
            </Spacing>
          ))}
        </Flex>
      </Spacing>
    )
  }

  return (
    <>
      <Script id={AC_URL.ICO_SCRIPT_ID} src={ICO_SCRIPT_URL} onError={handleIcoScriptError} />
      <CarsBannerContainer>
        <CarsBannerBgImage />
        <CarsBannerContentWrapper>
          <CarsBannerTextWrapper>
            <CarsBannerHeadlineText
              forwardedAs="h2"
              color={colors.white}
              textAlign={{ small: 'center', medium: 'left' }}
            >
              {t('top_cars_trucks.banner.headline.1')}&nbsp;
              <CarsBannerHeadlineSubText>
                {t('top_cars_trucks.banner.headline.2')}
              </CarsBannerHeadlineSubText>
              —{t('top_cars_trucks.banner.headline.3')}
            </CarsBannerHeadlineText>
            <CarsBannerBodyText
              size={{ small: 'large', medium: 'xLarge' }}
              color={colors.white}
              textAlign={{ small: 'center', medium: 'left' }}
            >
              {t('top_cars_trucks.banner.body')}
            </CarsBannerBodyText>
          </CarsBannerTextWrapper>
          <CarsBannerTabsOuterWrapper>
            <TopCarsBannerTabs
              linkList={[
                {
                  id: 'find-car-tab-anchor',
                  body: <h3>{t('top_cars_trucks.tabs.find_car.label')}</h3>,
                  href: '',
                  isSelected: tab === TAB1_ID,
                  onClick: (e) => handleClickTab(e, TAB1_ID),
                },
                {
                  id: 'sell-car-tab-anchor',
                  body: <h3>{t('top_cars_trucks.tabs.sell_car.label')}</h3>,
                  href: '',
                  isSelected: tab === TAB2_ID,
                  onClick: (e) => handleClickTab(e, TAB2_ID),
                },
              ]}
            />
            <Spacing
              p={spacing.default}
              medium={{
                pTop: spacing.husky,
                pBottom: spacing.husky,
                pLeft: spacing.large,
                pRight: spacing.large,
              }}
            >
              {tab === TAB1_ID && children}
              {tab === TAB2_ID && sellYourCar()}
            </Spacing>
          </CarsBannerTabsOuterWrapper>
        </CarsBannerContentWrapper>
      </CarsBannerContainer>
    </>
  )
}
